import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { isApiError, siteService } from "@safers/api";
import { controlKeys } from "@/pages/control/useControlData";

/** 404 화면이 "어떤 페이지" 대신 "현장" 을 못 찾았다고 말하게 하는 표시. */
export const SITE_NOT_FOUND = { notFound: "site" } as const;

/**
 * 없는 현장 주소는 404 로 보낸다.
 *
 * 즐겨찾기·공유 링크의 현장이 지워지면 화면마다 스켈레톤이 끝없이 돈다 — 현장 조회가
 * 404 인데 각 칸은 그걸 "아직 안 왔다" 로만 읽는다. 권한(403)은 앞단 게이트가 이미
 * 거르므로 여기서는 404 만 본다.
 *
 * 확인하는 동안에는 화면을 그대로 그린다 — 있는 현장(대부분)을 한 박자 늦추지 않는다.
 * 관제 화면과 같은 쿼리 키를 써서 현장 조회는 한 번만 나간다.
 */
export function SiteNotFoundGate({
  siteId,
  children,
}: {
  siteId: number | null;
  children: ReactNode;
}) {
  const meta = useQuery({
    queryKey: controlKeys.siteMeta(siteId ?? -1),
    queryFn: () => siteService.get(siteId as number),
    enabled: siteId != null,
    staleTime: Infinity,
    refetchOnWindowFocus: "always",
  });

  // 셸 밖의 404 로 보낸다 — 셸 안에 두면 머리줄의 현장 동작(알림 보내기·작업자 QR)이
  // 없는 현장을 대상으로 그대로 살아 있다.
  if (siteId != null && isApiError(meta.error) && meta.error.status === 404) {
    return <Navigate to="/404" replace state={SITE_NOT_FOUND} />;
  }
  return <>{children}</>;
}
